/**
 * Layout and mark geometry for the station-volumes chart (spec §4b, §5).
 *
 * Everything here is pure: no React, no DOM, no scales. The component owns
 * the scales and the event handling; this module owns the arithmetic that
 * decides how big a bar is, where a label may go and which equations are in
 * range — so it can be asserted directly and so the PDF report section can
 * reuse the same grammar without importing a React tree.
 */
import { internalToDisplayRaw } from '@/core/alignment';

import type { StationSectionLine, StationVolumesJson } from './types';

/**
 * Plot margins. `left` carries the m³ tick labels (both panes share the
 * gutter so their zero lines stack), `bottom` the shared station axis plus
 * the equation / low-coverage tick marks under it.
 */
export const CHART_MARGIN = { top: 10, right: 14, bottom: 28, left: 56 } as const;

/** Bars are capped so they never fill their band — the leftover band is air. */
export const MAX_BAR_PX = 24;
/** The surface-coloured gap between neighbouring bars (spec §5). */
export const BAR_GAP_PX = 2;
/** Rounded at the data end, square at the zero baseline (spec §5). */
export const BAR_RADIUS_PX = 4;
/** Breathing room between the bar pane and the mass-haul pane. */
export const PANE_GAP_PX = 10;
/**
 * Font size of every tick label and direct label in the chart. It lives here
 * rather than beside the marks because `BAR_LABEL_HEADROOM_PX` below is
 * derived from it; `StationVolumeChartParts` re-exports it.
 */
export const TICK_FONT = 10;
/** Direct labels closer together than this collide, so the later one is dropped. */
export const LABEL_MIN_GAP_PX = 64;
/** Equation tags read `1+020 → 1+500`, so they need more room than a balance tag. */
export const EQUATION_LABEL_MIN_GAP_PX = 96;

export interface ChartLayout {
  innerWidth: number;
  /** Height of the bar pane. In compact mode this is the whole plot. */
  barHeight: number;
  /** Height of the mass-haul pane. `0` in compact mode. */
  haulHeight: number;
  margin: { top: number; right: number; bottom: number; left: number };
}

/**
 * Two stacked panes on one shared station axis, 2 : 1 in height.
 *
 * Not a secondary y-axis: on a dual-scale axis the crossing point of the two
 * series is an artefact of the scales, and on a mass-haul chart that crossing
 * point looks exactly like a balance point (spec §4b).
 */
export function chartLayout(width: number, height: number, compact: boolean): ChartLayout {
  const innerWidth = Math.max(1, width - CHART_MARGIN.left - CHART_MARGIN.right);
  const innerHeight = Math.max(1, height - CHART_MARGIN.top - CHART_MARGIN.bottom);
  const haulHeight = compact ? 0 : Math.round(innerHeight / 3);
  return { innerWidth, barHeight: innerHeight - haulHeight, haulHeight, margin: CHART_MARGIN };
}

/**
 * How many bars fit: 1 px of bar plus the 2 px surface gap the grammar
 * requires. Above this the bins are aggregated by summation — exact for
 * volumes, so nothing is resampled (spec §4b).
 */
export function maxBarsFor(innerWidth: number): number {
  return Math.max(1, Math.floor(innerWidth / 3));
}

/** Bar width for `nBars` bars across `innerWidth`, capped and gapped. */
export function barWidthFor(innerWidth: number, nBars: number): number {
  if (nBars <= 0) return 1;
  const band = innerWidth / nBars;
  return Math.max(1, Math.min(MAX_BAR_PX, band - BAR_GAP_PX));
}

/**
 * A bar rounded at the data end only. `<rect rx>` would round the zero
 * baseline too, which reads as a bar floating off its own axis.
 *
 * `round: 'none'` is the low-coverage case — spec §4b explicitly drops the
 * rounded cap there so the bar is distinguishable in greyscale print.
 */
export function barPath(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  round: 'top' | 'bottom' | 'none',
): string {
  const rr = round === 'none' ? 0 : Math.max(0, Math.min(r, w / 2, h));
  if (rr === 0) return `M${x},${y}h${w}v${h}h${-w}Z`;
  const mid = w - 2 * rr;
  if (round === 'top') {
    return `M${x},${y + rr}a${rr},${rr} 0 0 1 ${rr},${-rr}h${mid}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - rr}h${-w}Z`;
  }
  return `M${x},${y}h${w}v${h - rr}a${rr},${rr} 0 0 1 ${-rr},${rr}h${-mid}a${rr},${rr} 0 0 1 ${-rr},${-rr}Z`;
}

/**
 * A short axis tick for a cubic-metre value.
 *
 * Deliberately NOT `formatM3` (G9): that rule is for figures a reader quotes,
 * and three decimals on every gridline is noise. Ticks trade precision for
 * legibility, and the exact figure is one hover away in the tooltip.
 */
export function tickM3(v: number): string {
  const a = Math.abs(v);
  if (a < 1e-9) return '0';
  if (a >= 1000) return `${(v / 1000).toFixed(a >= 10000 ? 0 : 1)}k`;
  return v.toFixed(a >= 100 ? 0 : 1);
}

/**
 * Greedy left-to-right label thinning: keep a label only if it clears
 * `minGapPx` from the last one kept. The first and last positions always
 * keep theirs — spec §4b: "if two labels collide, only the extremes are
 * labelled and the rest go to the tooltip".
 */
export function thinLabels(xs: number[], minGapPx: number): boolean[] {
  const keep = xs.map(() => false);
  const n = xs.length;
  if (n === 0) return keep;
  let lastKept = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < n; i++) {
    if (xs[i] - lastKept >= minGapPx) {
      keep[i] = true;
      lastKept = xs[i];
    }
  }
  keep[0] = true;
  // The last position carries a label too — but measured against the last
  // label actually KEPT, not against `xs[0]`. Measuring against the first is
  // how `[0, 100, 105]` ends up printing 100 and 105 on top of each other.
  if (n > 1 && xs[n - 1] - lastKept >= minGapPx) keep[n - 1] = true;
  return keep;
}

export interface EquationMarker {
  /** Internal station the marker sits at. */
  station: number;
  /** Display station immediately BEFORE the jump. */
  from: number;
  /** Display station immediately after — the equation's `staAhead`. */
  to: number;
}

/**
 * Station equations inside the analysed range, each with the display jump it
 * introduces (`2+400 → 2+500`). `from` is sampled a hair before the equation
 * so it carries every earlier equation's shift but not this one's.
 */
export function equationMarkers(json: StationVolumesJson): EquationMarker[] {
  const { staStart, equations } = json.alignment;
  const { stationFromInternalM: lo, stationToInternalM: hi } = json.params;
  return (equations ?? [])
    .filter((e) => e.staInternal >= lo && e.staInternal <= hi)
    .map((e) => ({
      station: e.staInternal,
      // Round away the 1e-6 probe offset; detailed stations are quoted to 3 dp.
      from: Math.round(internalToDisplayRaw(e.staInternal - 1e-6, staStart, equations) * 1000) / 1000,
      to: e.staAhead,
    }));
}

/**
 * The end-area series: cut and fill in m² at each station LINE.
 *
 * A polyline through the station lines, not bars: an end area is a quantity
 * measured AT a chainage, and a bar spanning the interval between two of them
 * would draw a step where the data is a profile.
 *
 * Returns `''` for a side with fewer than two finite points — a lone point is
 * not a line, and an invalid station line (nothing measured on that plane)
 * breaks the run rather than being drawn through, the same rule
 * `sectionModel.validRuns` applies across a section.
 */
export function endAreaPaths(
  stations: readonly { staInternalM: number; cutAreaM2: number; fillAreaM2: number; valid: boolean }[],
  x: (station: number) => number,
  y: (area: number) => number,
): { cut: string; fill: string } {
  const build = (pick: (s: (typeof stations)[number]) => number): string => {
    let d = '';
    // Accumulated per RUN, and flushed only when the run has two points: a
    // lone measured station between two gaps is a dot, not a line, and
    // emitting its `moveto` alone would leave a stray command in the path.
    let run: string[] = [];
    const flush = () => {
      if (run.length >= 2) d += `M${run.join('L')}`;
      run = [];
    };
    for (const s of stations) {
      const v = pick(s);
      const px = x(s.staInternalM);
      const py = y(v);
      if (!s.valid || !Number.isFinite(v) || !Number.isFinite(px) || !Number.isFinite(py)) {
        flush();
        continue;
      }
      run.push(`${px.toFixed(2)},${py.toFixed(2)}`);
    }
    flush();
    return d;
  };
  return { cut: build((s) => s.cutAreaM2), fill: build((s) => s.fillAreaM2) };
}

/** The m² domain both end-area series share: `[0, max]`, never negative —
 *  an area has no sign, the two sides are two series. */
export function endAreaDomain(
  stations: readonly { cutAreaM2: number; fillAreaM2: number; valid: boolean }[],
): [number, number] {
  let max = 0;
  for (const s of stations) {
    if (!s.valid) continue;
    if (Number.isFinite(s.cutAreaM2) && s.cutAreaM2 > max) max = s.cutAreaM2;
    if (Number.isFinite(s.fillAreaM2) && s.fillAreaM2 > max) max = s.fillAreaM2;
  }
  // A flat-zero series still needs a domain with height, or every tick lands
  // on the same pixel and the axis prints one label four times.
  return [0, max > 0 ? max : 1];
}

/**
 * Room reserved ABOVE the tallest bar and BELOW the deepest one so the direct
 * label on an extreme bar has somewhere to sit.
 *
 * Derived from the label instead of picked as a percentage of the pane: a
 * flat 5 % is too little on a tall pane and too much on a short one, whereas
 * the text is the same 10 px either way. `BarPane` puts the fill label's
 * baseline 4 px above the bar top, so it needs that 4 px gap plus its own em
 * box (`TICK_FONT`); the cut label's 10 px baseline drop plus its descender
 * fits in the same budget. 2 px of slack on top.
 */
export const BAR_LABEL_HEADROOM_PX = TICK_FONT + 6;

/**
 * The bar pane's y-domain: the data's own `[-maxCut, +maxFill]`, grown by the
 * label headroom, and deliberately NOT forced symmetric.
 *
 * Symmetry (`[-vMax, +vMax]`) was the old behaviour and it cost twice:
 * a one-sided corridor spent half the pane on a side with no data (a run
 * with 0.4 m³ of cut and 284 m³ of fill was drawn on `[-284, +284]`), and
 * because `vMax` IS the maximum the tallest bar landed exactly on the pane
 * ceiling — which is where that bar's own label needed to go, so the label
 * clamped down on top of the bar and became unreadable.
 *
 * An asymmetric domain does NOT hurt cut-versus-fill comparability. A LINEAR
 * scale is uniform across its domain, so 10 m³ of cut and 10 m³ of fill are
 * still drawn the same number of pixels tall; only the zero line moves off
 * centre. "Keep it symmetric so the two sides stay comparable" is the
 * plausible-sounding reason someone will give for reverting this, and it is
 * wrong for a linear scale — do not restore the symmetric domain.
 *
 * (`symmetricDomain` in `aggregate.ts` is a different thing and is correctly
 * symmetric: it feeds the 3D band colour ramp, a DIVERGING ramp whose
 * neutral midpoint has to land on zero.)
 *
 * Zero is always in the domain: the baseline is the datum the pane is read
 * against, and a bar chart that crops it misleads.
 */
export function barDomain(
  bars: readonly { cutVolumeM3: number; fillVolumeM3: number }[],
  paneHeight: number,
): [number, number] {
  let maxCut = 0;
  let maxFill = 0;
  for (const b of bars) {
    if (Number.isFinite(b.cutVolumeM3) && b.cutVolumeM3 > maxCut) maxCut = b.cutVolumeM3;
    if (Number.isFinite(b.fillVolumeM3) && b.fillVolumeM3 > maxFill) maxFill = b.fillVolumeM3;
  }
  // Nothing measured: still needs height, or every tick lands on one pixel.
  if (maxCut <= 0 && maxFill <= 0) return [-1, 1];

  const h = Math.max(1, paneHeight);
  // Never spend more than a fifth of the pane per side on headroom: on a dock
  // dragged to its 180 px minimum the label is a lost cause and the bars are
  // what the reader still has.
  const pad = Math.min(BAR_LABEL_HEADROOM_PX, h * 0.2);
  // A label is only drawn on a side that has data, so only that side pays.
  const sides = (maxCut > 0 ? 1 : 0) + (maxFill > 0 ? 1 : 0);
  // The headroom is specified in PIXELS but has to be added in DATA units,
  // and the conversion depends on the span it is being added to. Solving
  // `span = data + sides * pad * span / h` for span closes the loop; the
  // 0.2 cap above keeps the denominator >= 0.6.
  const span = (maxCut + maxFill) / (1 - (sides * pad) / h);
  const extra = (span * pad) / h;
  return [maxCut > 0 ? -(maxCut + extra) : 0, maxFill > 0 ? maxFill + extra : 0];
}

/**
 * One crossfall series' path through the station lines, broken at every
 * unresolved station.
 *
 * The same run/flush rule `endAreaPaths` uses, and for the same reason: a
 * lone measured station between two gaps is a dot, not a line, and emitting
 * its `moveto` alone would leave a stray command in the path.
 *
 * `pick` returning `null` is what breaks the run, and the CALLER decides what
 * counts as unresolved — see `crossfallPaths` below. Do NOT read this as "an
 * INCONCLUSIVE station has no figure": on the UNCERTAIN path
 * `scripts/crossfall.py` computes a real `asbuiltPct` and only then withholds
 * the verdict, so the as-built series gaps on the VERDICT while the design
 * series, whose geometry is still sound, does not. Only the COVERAGE path
 * nulls the figures.
 */
const crossfallRun = (
  stations: readonly StationSectionLine[],
  pick: (l: StationSectionLine) => number | null | undefined,
  x: (station: number) => number,
  y: (pct: number) => number,
): string => {
  let d = '';
  let run: string[] = [];
  const flush = () => {
    if (run.length >= 2) d += `M${run.join('L')}`;
    run = [];
  };
  for (const s of stations) {
    const v = pick(s);
    const px = x(s.staInternalM);
    const py = v == null ? NaN : y(v);
    if (v == null || !Number.isFinite(px) || !Number.isFinite(py)) {
      flush();
      continue;
    }
    run.push(`${px.toFixed(2)},${py.toFixed(2)}`);
  }
  flush();
  return d;
};

/** A CLOSED band of ±`tol` about a design series — the tolerance the verdict
 *  was struck against, drawn so the reader can see an as-built line leave it
 *  rather than having to read the verdict glyph in the table. Broken at the
 *  same gaps as the line it surrounds. */
const crossfallToleranceBand = (
  stations: readonly StationSectionLine[],
  pick: (l: StationSectionLine) => number | null | undefined,
  x: (station: number) => number,
  y: (pct: number) => number,
  tol: number,
): string => {
  let d = '';
  let upper: string[] = [];
  let lower: string[] = [];
  const flush = () => {
    if (upper.length >= 2) d += `M${upper.join('L')}L${lower.reverse().join('L')}Z`;
    upper = [];
    lower = [];
  };
  for (const s of stations) {
    const v = pick(s);
    const px = x(s.staInternalM);
    if (v == null || !Number.isFinite(px) || !Number.isFinite(y(v + tol)) || !Number.isFinite(y(v - tol))) {
      flush();
      continue;
    }
    upper.push(`${px.toFixed(2)},${y(v + tol).toFixed(2)}`);
    lower.push(`${px.toFixed(2)},${y(v - tol).toFixed(2)}`);
  }
  flush();
  return d;
};

/** Every path the crossfall pane draws: as-built L/R (solid), design L/R
 *  (dashed) and the two tolerance bands. */
export function crossfallPaths(
  stations: readonly StationSectionLine[],
  x: (station: number) => number,
  y: (pct: number) => number,
  tolerancePct = 0,
): {
  asbuiltLeft: string;
  asbuiltRight: string;
  designLeft: string;
  designRight: string;
  toleranceLeft: string;
  toleranceRight: string;
} {
  /**
   * The AS-BUILT series gaps on the VERDICT, not on a null figure — the two are
   * not the same set. `scripts/crossfall.py` nulls every figure only on the
   * `_blank()` COVERAGE path; on the UNCERTAIN path (Δ's confidence interval
   * spans both the pass band and the fail band, so the fit cannot tell one
   * from the other — spec §12.1) it sets
   * `verdict = INCONCLUSIVE` *after* computing real `asbuiltPct` /
   * `designPct`. Keying off `asbuiltPct == null` therefore drew an unresolved
   * fit as an ordinary solid segment, indistinguishable from a PASS, while the
   * Table tab in the same dock printed "the fit could not resolve this band"
   * for it. The number exists; what it does not have is standing.
   */
  const ab = (side: 'left' | 'right') => (l: StationSectionLine) => {
    const s = l.crossfall?.[side];
    return !s || s.verdict === 'INCONCLUSIVE' ? null : (s.asbuiltPct ?? null);
  };
  /**
   * The DESIGN series does NOT gap on the verdict. An UNCERTAIN station's
   * design fit and the tolerance band around it are still valid reference
   * geometry — the uncertainty is in the measurement, not in the design — and
   * breaking them there would delete good context the reader needs precisely
   * where the as-built line disappears.
   */
  const de = (side: 'left' | 'right') => (l: StationSectionLine) => l.crossfall?.[side]?.designPct ?? null;
  return {
    asbuiltLeft: crossfallRun(stations, ab('left'), x, y),
    asbuiltRight: crossfallRun(stations, ab('right'), x, y),
    designLeft: crossfallRun(stations, de('left'), x, y),
    designRight: crossfallRun(stations, de('right'), x, y),
    toleranceLeft: tolerancePct > 0 ? crossfallToleranceBand(stations, de('left'), x, y, tolerancePct) : '',
    toleranceRight: tolerancePct > 0 ? crossfallToleranceBand(stations, de('right'), x, y, tolerancePct) : '',
  };
}

/**
 * The percent domain the crossfall pane shares.
 *
 * Spans every as-built AND design value, plus the tolerance band about the
 * design — a band drawn outside the axis is a band the reader cannot see the
 * as-built line leave. Not symmetric about zero: a crowned road never reaches
 * positive crossfall on either side, and forcing zero into view would squash
 * the ±0.3 % the check is actually about into a pixel.
 */
export function crossfallDomain(
  stations: readonly StationSectionLine[],
  tolerancePct: number,
): [number, number] {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  const see = (v: number | null | undefined, pad = 0) => {
    if (v == null || !Number.isFinite(v)) return;
    lo = Math.min(lo, v - pad);
    hi = Math.max(hi, v + pad);
  };
  for (const s of stations) {
    for (const side of ['left', 'right'] as const) {
      see(s.crossfall?.[side]?.asbuiltPct);
      see(s.crossfall?.[side]?.designPct, Math.max(tolerancePct, 0));
    }
  }
  // A run where nothing resolved, and a perfectly flat one, both need a
  // domain with height or every tick lands on the same pixel.
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [-1, 1];
  if (hi - lo < 1e-9) return [lo - 1, hi + 1];
  const pad = (hi - lo) * 0.08;
  return [lo - pad, hi + pad];
}
